// Peer comparison against the published benchmark file (src/data/benchmarks.json).
// Pure functions: the data is passed in, so Node can test them (scripts/benchmarks.test.mjs).
// Everything is compared in marks, the unit the peer figures are stored in, and only
// converted to percentages for display.
import { INDEX_GROUP, INDUSTRY_GROUP } from "../data/benchmarks.js";

export const TISC_CATEGORY = "TISC/International Template";

// The backend rounds percentages to 2 decimals, so a valid score is within 0.005 of marks/max
const ROUNDING_TOLERANCE = 0.005 + 1e-9;

export function resolveFrameworkType(analysis) {
  const meta = analysis?.analysis_metadata || {};
  return meta.framework_type || analysis?.framework_type || meta.framework?.type || "current";
}

function parseVersion(value) {
  const match = /^v(\d+)\.(\d+)$/.exec(String(value ?? "").trim());
  return match ? [Number(match[1]), Number(match[2])] : null;
}

// Scores from before min_model_version may be on another scale or unvalidated, and the
// numbers alone can't show it (35/70 = 50% would pass as 29/58), so the version decides
export function isComparable(analysis, data) {
  const version = parseVersion(analysis?.analysis_metadata?.model_version);
  const minimum = parseVersion(data?.meta?.min_model_version);
  if (!version || !minimum) return false;
  return version[0] > minimum[0] || (version[0] === minimum[0] && version[1] >= minimum[1]);
}

// The whole number of marks behind a percentage score, or null if it isn't one
export function toMarks(scorePct, max) {
  if (typeof scorePct !== "number" || !Number.isFinite(scorePct) || scorePct < 0 || scorePct > 100) return null;
  if (!Number.isInteger(max) || max <= 0) return null;
  const marks = Math.round((scorePct * max) / 100);
  return Math.abs((marks * 100) / max - scorePct) <= ROUNDING_TOLERANCE ? marks : null;
}

// Mid-rank percentile: peers scoring lower count fully, peers with the same score count half
export function rank(userMarks, marks) {
  let below = 0;
  let equal = 0;
  for (const value of marks) {
    if (value < userMarks) below += 1;
    else if (value === userMarks) equal += 1;
  }
  const n = marks.length;
  return { below, equal, n, percentile: n ? ((below + 0.5 * equal) / n) * 100 : null };
}

export function position(userMarks, { q1, median, q3 }) {
  if (userMarks === median) return "at_median";
  if (userMarks < q1) return "below_q1";
  if (userMarks < median) return "q1_to_median";
  if (userMarks <= q3) return "median_to_q3";
  return "above_q3";
}

export function ordinal(value) {
  const n = Math.trunc(value);
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
}

// Text for a rank. The extremes come from the counts, not the rounded percentile
export function describeRank({ below, equal, n, percentile }, groupLabel) {
  if (equal === 0 && below === 0) return { extreme: true, text: `Lower than every ${groupLabel} peer` };
  if (equal === 0 && below === n) return { extreme: true, text: `Higher than every ${groupLabel} peer` };
  if (percentile < 0.5) return { extreme: false, text: "Below the 1st percentile" };
  if (percentile > 99.5) return { extreme: false, text: "Above the 99th percentile" };
  return { extreme: false, text: `${ordinal(Math.round(percentile))} percentile` };
}

const wholePct = (value) => `${Math.round(value)}%`;

// Advice that follows the rank sentence on the percentile band
export function guidanceText(comparison) {
  const median = formatPct(comparison.median_pct);
  const q3 = wholePct(comparison.q3_pct);
  switch (comparison.position) {
    case "above_q3":
      return "Keep codifying good practice across the programme.";
    case "median_to_q3":
      if (comparison.user_marks === comparison.q3) {
        return `Above the median of ${median}, at the top-quartile mark (${q3}). Focus on the priority improvements to move clear of it.`;
      }
      return `Above the median of ${median}. Focus on the priority improvements to close the gap to the top quartile (${q3}).`;
    case "at_median":
      if (comparison.median === comparison.q3) {
        return `At the median of ${median}, which is also the top-quartile mark. Focus on the priority improvements to move clear of it.`;
      }
      return `At the median of ${median}. Focus on the priority improvements to close the gap to the top quartile (${q3}).`;
    case "q1_to_median":
      return `Below the median of ${median}. Accelerate the near-term recommendations.`;
    default:
      return `Below the bottom-quartile mark of ${wholePct(comparison.q1_pct)}. Prioritise quick wins.`;
  }
}

const toPct = (marks, max) => (marks * 100) / max;

function compareMetric(scorePct, metric) {
  if (!metric) return null;
  const userMarks = toMarks(scorePct, metric.max);
  if (userMarks === null) return null;
  const r = rank(userMarks, metric.marks);
  return {
    max: metric.max,
    user_marks: userMarks,
    user_pct: toPct(userMarks, metric.max),
    median: metric.median,
    q1: metric.q1,
    q3: metric.q3,
    mean: metric.mean,
    median_pct: toPct(metric.median, metric.max),
    q1_pct: toPct(metric.q1, metric.max),
    q3_pct: toPct(metric.q3, metric.max),
    mean_pct: toPct(metric.mean, metric.max),
    rank: r,
    lower_pct: (r.below / r.n) * 100,
    same_pct: (r.equal / r.n) * 100,
    position: position(userMarks, metric),
  };
}

function compareGroup(key, analysis, data, metricName, comparable) {
  const group = key ? data?.groups?.[key] : null;
  if (!group) return null;
  const base = { key, label: group.label, kind: group.kind };
  if (group.insufficient) return { ...base, insufficient: true };
  if (!comparable) return { ...base, unavailable: true };
  const overall = compareMetric(analysis?.overall_score, group[metricName]);
  if (!overall) return { ...base, unavailable: true };
  const result = { ...base, overall, category_means: group[metricName].category_means };
  if (metricName === "advanced") {
    result.tisc = compareMetric(analysis?.category_scores?.[TISC_CATEGORY], group.tisc);
  }
  return result;
}

// Plain, serialisable comparison for the industry and index chosen on the upload form
export function getPeerComparison({ analysis, data, industry, index }) {
  const frameworkType = resolveFrameworkType(analysis);
  const metricName = frameworkType === "advanced" ? "advanced" : "current";
  const comparable = isComparable(analysis, data);
  return {
    release: data?.meta?.release ?? null,
    generated: data?.meta?.generated ?? null,
    framework_type: frameworkType,
    industry: compareGroup(INDUSTRY_GROUP[industry], analysis, data, metricName, comparable),
    index: compareGroup(INDEX_GROUP[index], analysis, data, metricName, comparable),
  };
}

// Figures to show for a percentage: one decimal, as elsewhere on the results page
export const formatPct = (value) => `${value.toFixed(1)}%`;

export function formatMarks(value) {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}
